import Button from '@orn/shared-web/components/Button/Button';
import { ArrowNarrowLeftIcon } from '@orn/shared-web/icons';

import type { ReactNode } from 'react';

interface IOnboardingTitleProps {
  /**
   * Title text shown in the brand serif. Accepts `ReactNode` so steps can
   * inline an image (e.g. a Plaid logo) without rebuilding the
   * back-arrow + heading scaffold from scratch.
   */
  title: ReactNode;
  /**
   * Optional text or element rendered directly below the title, inside the
   * same 12px-gap group. A plain `string` is wrapped in the standard tertiary
   * paragraph styling; a `ReactNode` is rendered as-is so callers can slot in
   * status banners, links, or any other rich content without losing the tight
   * title-adjacent spacing.
   */
  subtitle?: ReactNode;
  /**
   * When provided, renders the standardized back-button (tertiary icon-only)
   * directly above the title. Each step passes its `onBack` handler through
   * here rather than rendering its own back button.
   */
  onBack?: () => void;
}

/**
 * Renders the title + optional subtitle pair shown above each onboarding/login
 * step. Left-aligned per the Figma onboarding spec; the brand serif is used for
 * the title to match the rest of the marketing/auth surface.
 */
const OnboardingTitle = ({ title, subtitle, onBack }: IOnboardingTitleProps) => {
  return (
    <div className="flex w-full flex-col items-start gap-3">
      {onBack && (
        <Button
          color="tertiary"
          size="md"
          iconLeading={ArrowNarrowLeftIcon}
          onClick={onBack}
          aria-label="Go back"
          className="-ml-2.5 !rounded-lg"
        />
      )}
      <h1 className="text-primary font-arizona text-display-md w-full tracking-[-0.72px]">{title}</h1>
      {typeof subtitle === 'string' ? <p className="text-tertiary text-md w-full font-normal">{subtitle}</p> : subtitle}
    </div>
  );
};

OnboardingTitle.displayName = 'OnboardingTitle';

export default OnboardingTitle;
